import React from 'react';
import { renderToString } from 'react-dom/server';
import DevBudgetChangeLineChartTooltip from '../components/Tooltip';
import { formatMilestoneDateByStatus } from '@gp/utils/milestone';
import {
  BASE_LINE_CHART_TOOLTIP_AXIS_POINTER_OPTION,
  BASE_ECHART_OPTION,
  Y_AXIS_NAME_CURRENCY_OPTION,
  Y_AXIS_NAME_CURRENCY_GRID_OPTION,
} from '@gp/constants/echarts';

import type { IRecord } from '@gp/types/global';
import type { IDevBudgetChangeVO } from '@gp/types/finance';

const INCREASE_DECREASE_OPTIONS = {
  padding: [3, 2, 1, 2],
  width: 44,
  align: 'center',
  verticalAlign: 'middle',
  fontSize: 12,
  color: '#fff',
  borderRadius: 4,
};

const BASE_SERIES_OPTION = {
  type: 'line',
  symbol: 'circle',
  symbolSize: 8,
  label: {
    show: true,
    position: 'top',
    rich: {
      normal: {
        padding: [0, 0, 4, 0],
        align: 'center',
        color: '#525464',
      },
      increase: {
        ...INCREASE_DECREASE_OPTIONS,
        backgroundColor: '#F17C86',
      },
      decrease: {
        ...INCREASE_DECREASE_OPTIONS,
        backgroundColor: '#69D19C',
      },
    },
  },
  emphasis: {
    scale: false,
  },
};

const getDevBudgetChangeItemById = (list?: IDevBudgetChangeVO[], id?: string): IDevBudgetChangeVO | null => {
  if (!Array.isArray(list) || !list.length) return null;
  return list.find((item) => item?.milestone_id === id) || null;
};

export const getStaticLineOption = (cfg?: { valueFormatter?: (payload: any) => string }): IEChartOption => {
  const { grid, tooltip, xAxis, yAxis: yAxises, ...restOption } = BASE_ECHART_OPTION;
  const { valueFormatter } = cfg || {};
  // @ts-ignore
  const yAxis = yAxises?.[0] || {};
  return {
    ...restOption,
    grid: {
      ...grid,
      ...Y_AXIS_NAME_CURRENCY_GRID_OPTION,
      bottom: 20,
      height: 310,
    },
    tooltip: {
      ...tooltip,
      axisPointer: {
        ...BASE_LINE_CHART_TOOLTIP_AXIS_POINTER_OPTION,
      },
    },
    xAxis: {
      ...xAxis,
      axisLabel: {
        // @ts-ignore
        ...xAxis?.axisLabel,
        width: 160,
        align: 'center',
        overflow: 'truncate',
        ellipsis: '...',
        interval: 0,
        rotate: 0,
        rich: {
          name: {
            padding: [0, 0, 4, 0],
          },
        },
      },
    },
    yAxis: {
      ...yAxis,
      ...Y_AXIS_NAME_CURRENCY_OPTION,
      axisLabel: {
        ...yAxis?.axisLabel,
        formatter: (payload: any) => valueFormatter?.(payload) ?? payload,
      },
    },
  };
};

export const getDynamicLineOption = (
  list?: IDevBudgetChangeVO[],
  cfg?: { valueFormatter?: (payload: any) => string; percentageFormatter?: (payload: any) => string },
): IEChartOption => {
  if (!Array.isArray(list) || !list.length) return {};

  const { valueFormatter, percentageFormatter } = cfg || {};
  const xAxisData: string[] = [];
  const seriesData: string[] = [];

  for (const item of list) {
    const { milestone_id: milestoneId, budget } = item || {};
    xAxisData.push(milestoneId);
    seriesData.push(budget);
  }

  return {
    tooltip: {
      formatter: (params: any) => {
        const { name } = params?.[0] || {};
        const item = getDevBudgetChangeItemById(list, name);

        return renderToString(
          <DevBudgetChangeLineChartTooltip
            title="Approved Total Development Budget"
            data={item}
            valueFormatter={valueFormatter}
          />,
        );
      },
    },
    xAxis: {
      data: xAxisData,
      axisLabel: {
        formatter: (value: string) => {
          const item = getDevBudgetChangeItemById(list, value);
          const { milestone, date_confirm: dateConfirm, date } = item || {};
          const dateFormatted = formatMilestoneDateByStatus(date, dateConfirm);
          return `{name|${milestone}}\n${dateFormatted}`;
        },
      },
    },
    series: [
      {
        ...BASE_SERIES_OPTION,
        data: seriesData,
        // @ts-ignore
        label: {
          ...BASE_SERIES_OPTION.label,
          formatter: (params: IRecord<any>) => {
            const { name, value } = params || {};
            const valueFormatted = valueFormatter?.(value) ?? value;
            const item = getDevBudgetChangeItemById(list, name);
            const { budget_change: budgetChange } = item || {};
            if (!budgetChange) return `{normal|${valueFormatted}}`;
            const richKey = budgetChange > 0 ? 'increase' : 'decrease';
            const budgetChangeFormatted = percentageFormatter?.(budgetChange) ?? budgetChange;
            return `{normal|${valueFormatted}}\n{${richKey}|${budgetChangeFormatted}}`;
          },
        },
      },
    ],
  };
};
